import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';

import { useEnumPreload } from '../../hooks/useEnum';
import { sortByAlphabet } from '../../model/Enum';
import { useRouteContext } from '../../RouteProvider';
import type { Country } from '../../services/Country';
import type { Language } from '../../services/Language';
import { closeModal, setLanguage } from '../../store/app';
import { isSignedInSelector } from '../../store/selectors';
import { useTranslationContext } from '../../TranslationProvider';
import { RegionRadioField } from '../Form/CountryRadioGroup';
import { RadioField } from '../Form/Radio';
import FormikSubmitButton from '../Form/SubmitButton';
import Modal from '../Modal';
import { useRegion } from '../Restriction';
import T from '../Translate';

interface FormValues {
  region: Country;
  language: Language;
}

export default function RegionSelectDialog() {
  const dispatch = useDispatch();
  const [region, setRegion] = useRegion();
  const isSignedIn = useSelector(isSignedInSelector);
  const { language, languages, changeLanguage } = useTranslationContext();
  const { pathWithoutPrefix, location, navigate } = useRouteContext();
  useEnumPreload(['regions']);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (values.language === language && values.region === region) {
        dispatch(closeModal(false));
        return;
      }
      if (values.language !== language) {
        navigate(`/${values.language}${pathWithoutPrefix}${location.search}${location.hash}`, { withoutPrefix: true });
        changeLanguage(values.language);
      }
      if (values.region !== region) {
        setRegion(values.region);
      }
      await dispatch(setLanguage(values.language, values.region));
      dispatch(closeModal());
    },
    [language, region],
  );

  return (
    <Modal closeModal={() => dispatch(closeModal(false))} size="lg">
      <div className="modal-body">
        <Formik initialValues={{ region, language }} onSubmit={handleSubmit}>
          <Form>
            {!isSignedIn && (
              <>
                <T as="h3" id="modals.regionSelect.region.title" />
                <RegionRadioField inline name="region" />
              </>
            )}

            <T as="h3" className="mt-3" id="modals.regionSelect.language.title" />
            <div className="form-inline">
              {sortByAlphabet(languages.map(lng => ({ value: lng, label: lng }))).map(option => (
                <RadioField className="radio--bordered" key={option.value} name="language" value={option.value}>
                  <T id={`global.language.${option.value}`} />
                </RadioField>
              ))}
            </div>

            <FormikSubmitButton className="mt-5" outline>
              <T id="modals.regionSelect.submit" />
            </FormikSubmitButton>
          </Form>
        </Formik>
      </div>
    </Modal>
  );
}
